import { useShoppingCart } from "../context/ShoppingCartContext";
import {
  formatDiscountLineAmount,
  pf,
  resolveProductImageUrl,
} from "../utilities/helpers";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import usePageLanguage from "../hooks/usePageLanguage";
import { resolveCatalogProductPricing } from "../utilities/catalogProductAdapter";
import { getAdjustmentDisplayName } from "../utilities/locationProductAdjustments";
import { getEffectivePrice } from "../utilities/productCatalog";
import {
  clampQuantityToMinAndCap,
  resolveMinOrderQty,
} from "../utilities/quantityCaps";
import React, { useMemo } from "react";
import type { CartLineIdentity } from "../utilities/cartIdentity";

const EXTRA_LINE_INDENT_PX = 14;

const removedFromSaleTranslations = {
  et: "Toode ei ole enam müügis",
  en: "Product removed from sale",
};

type CartItemProps = {
  productUuid: string;
  quantity: number;
  readOnly?: boolean;
  maxAvailable?: number;
  maxOrderQty?: number;
  minOrderQty?: number;
  deleteHidden?: boolean;
  start?: string;
  eventHash?: string;
  /** Synced default extras from parent product; quantity is driven by parent. */
  cartLineSource?: CartLineIdentity["cartLineSource"];
  parentProductUuids?: string[];
  invalid?: boolean;
  productName?: string;
};

export function CartItemRow({
  productUuid,
  quantity,
  readOnly = false,
  deleteHidden,
  eventHash,
  start,
  cartLineSource,
  parentProductUuids,
  invalid = false,
  productName,
  minOrderQty,
}: CartItemProps) {
  const {
    getCatalogProductByUuid,
    getCatalogEntryByUuid,
    products,
    pricingNow,
    decreaseCartQuantity,
    setCartQuantity,
    increaseCartQuantity,
    removeFromCart,
    getCartQuantityCap,
    getCartMinOrderQty,
  } = useShoppingCart();
  const parentMappedProduct = getCatalogProductByUuid(productUuid);
  const lang = usePageLanguage();
  const strictCatalogProduct = useMemo(
    () => products.find((p) => p.productUuid === productUuid),
    [products, productUuid]
  );

  const catalogEntry = useMemo(
    () => (productUuid ? getCatalogEntryByUuid(productUuid) : undefined),
    [productUuid, getCatalogEntryByUuid]
  );

  const linePricing = useMemo(() => {
    if (!catalogEntry) {
      return null;
    }
    return getEffectivePrice(catalogEntry, pricingNow);
  }, [catalogEntry, pricingNow]);

  const activeAdjustments = useMemo(() => {
    if (!catalogEntry || catalogEntry.variant) {
      return [];
    }
    return resolveCatalogProductPricing(catalogEntry.parent, pricingNow)
      .activeAdjustments;
  }, [catalogEntry, pricingNow]);

  const eventRef = eventHash;
  const isAutoExtra =
    cartLineSource === "autoExtra" ||
    (parentProductUuids?.length ?? 0) > 0;
  const maxAvailable = readOnly
    ? null
    : eventRef
      ? getCartQuantityCap(productUuid ?? "", eventRef)
      : null;
  const hasAvailabilityInfo =
    !readOnly && !isAutoExtra && eventRef && maxAvailable !== null;
  const minQty =
    eventRef && !isAutoExtra
      ? getCartMinOrderQty(productUuid ?? "", eventRef)
      : resolveMinOrderQty(minOrderQty);
  const extraIndentStyle = isAutoExtra
    ? { paddingLeft: EXTRA_LINE_INDENT_PX }
    : undefined;

  const displayName =
    strictCatalogProduct?.translations?.[lang]?.name ??
    productName ??
    parentMappedProduct?.translations?.[lang]?.name ??
    productUuid;
  const displayImageUrl =
    strictCatalogProduct?.firstFileUrl ?? parentMappedProduct?.firstFileUrl;
  const unitShortname =
    strictCatalogProduct?.unitShortname ??
    parentMappedProduct?.unitShortname ??
    "";

  if (invalid) {
    const hideDelete = deleteHidden || isAutoExtra;

    return (
      <div
        className={`cartOrderProductRow cartOrderProductRow--invalid${isAutoExtra ? " cartOrderProductRow--extra" : ""}${readOnly ? " cartOrderProductRow--readonly" : ""}`}
      >
        <div className="cartOrderProductRowImage">
          <img
            src={resolveProductImageUrl(displayImageUrl)}
            alt=""
            className="cartSidepanelProductImage"
          />
        </div>
        <div
          className={`cartOrderProductRowName${isAutoExtra ? " cartOrderProductRowName--extra" : ""}`}
          style={extraIndentStyle}
        >
          <span className="cartOrderProductRowTitle">{displayName}</span>
          <div
            className={`cartOrderProductRowDiscount${isAutoExtra ? " cartOrderProductRowDiscount--extra" : ""}`}
          >
            <span className="discount-price-color">
              {removedFromSaleTranslations[lang === "en" ? "en" : "et"]}
            </span>
          </div>
        </div>
        <div
          className={`cartOrderProductQuantity${readOnly ? " cartOrderProductQuantity--readonly" : ""}`}
          aria-hidden="true"
        />
        <div
          className="cartOrderProductRowPrice cartOrderProductRowPrice--regular"
          aria-hidden="true"
        />
        {!readOnly && (
          <div
            className="cartOrderProductRowDelete"
            style={{
              visibility: hideDelete ? "hidden" : "visible",
              pointerEvents: hideDelete ? "none" : "auto",
            }}
          >
            <FontAwesomeIcon
              icon={faTrashAlt}
              className="trash-icon"
              onClick={() => removeFromCart(productUuid ?? "", eventRef)}
            />
          </div>
        )}
      </div>
    );
  }

  // Check if we can increase quantity (respecting maxAvailable for event products)
  const canIncreaseQuantity = maxAvailable === null || quantity < maxAvailable;
  const qtyControlsDisabled = isAutoExtra;

  if (parentMappedProduct == null || linePricing == null) return null;

  const hideDelete = deleteHidden || isAutoExtra;

  const hasAdjustmentDisplay = activeAdjustments.length > 0;
  const lineDiscountAmount = linePricing.adjustmentAmount * quantity;
  const lineRegularTotal = linePricing.priceRegular * quantity;

  return (
    <div
      className={`cartOrderProductRow${isAutoExtra ? " cartOrderProductRow--extra" : ""}${hasAdjustmentDisplay ? " cartOrderProductRow--hasAdjustment" : ""}${readOnly ? " cartOrderProductRow--readonly" : ""}`}
    >
      <div
        className="cartOrderProductRowImage"
      >
        <img
          src={resolveProductImageUrl(displayImageUrl)}
          alt=""
          className="cartSidepanelProductImage"
        />
      </div>
      <div
        className={`cartOrderProductRowName${isAutoExtra ? " cartOrderProductRowName--extra" : ""}`}
      >
        <span className="cartOrderProductRowTitle">
          {displayName}
        </span>
        {/* Show maxAvailable for event products */}
        {hasAvailabilityInfo && (
          <span style={{ fontSize: "12px", color: "#000" }}>
            Saadaval: {maxAvailable}
          </span>
        )}
        {hasAdjustmentDisplay && (
          <div
            className={`cartOrderProductRowDiscount${isAutoExtra ? " cartOrderProductRowDiscount--extra" : ""}`}
          >
            <span className="discount-price-color">
              {activeAdjustments.map((adjustment, i) => {
                const value = String(adjustment.value);
                return `${i > 0 ? ", " : ""}${getAdjustmentDisplayName(adjustment)} ${
                  value.startsWith("-") ? "" : "+"
                }${value}%`;
              })}
            </span>
          </div>
        )}
      </div>
      <div
        className={`cartOrderProductQuantity${readOnly ? " cartOrderProductQuantity--readonly" : ""}`}
      >
        {readOnly ? (
          <div className="cartOrderProductQuantityReadonly">
            <span>{quantity}</span>
            <span> {unitShortname} </span>
          </div>
        ) : (
          <>
            <div
              className={`cartQtyStepper${isAutoExtra ? " cartQtyStepper--extra" : ""}`}
            >
          {!isAutoExtra && (
            <RemoveIcon
              className={`icon padding-5 fs-14${qtyControlsDisabled ? "" : " clickable"}`}
              aria-disabled={qtyControlsDisabled}
              style={{
                opacity: qtyControlsDisabled ? 0.35 : 1,
                cursor: qtyControlsDisabled ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                if (qtyControlsDisabled) return;
                decreaseCartQuantity(productUuid ?? "", eventRef);
              }}
            />
          )}

          <div className="cartQtyInputWrap">
            {/* <span>{quantity}</span> */}
            <input
              type="text"
              className="cartQtyInput"
              name="quantity"
              disabled={!!start || isAutoExtra}
              value={quantity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (isAutoExtra) return;
                const inputValue = e.target.value;

                // Allow empty input for typing purposes
                if (inputValue === "") {
                  return;
                }

                // Only allow numeric input
                const numericValue = parseInt(inputValue, 10);
                if (isNaN(numericValue)) {
                  return;
                }

                let newQuantity = numericValue;
                if (newQuantity < 0) newQuantity = 0;

                newQuantity = clampQuantityToMinAndCap(
                  newQuantity,
                  eventRef ? minQty : minOrderQty,
                  maxAvailable
                );

                setCartQuantity(newQuantity, productUuid ?? "", eventRef, start);
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (isAutoExtra) return;
                const inputValue = e.target.value;
                const numericValue = parseInt(inputValue, 10);

                if (inputValue === "" || isNaN(numericValue) || numericValue <= 0) {
                  if (eventRef && !isAutoExtra && minQty > 1) {
                    removeFromCart(productUuid ?? "", eventRef);
                  } else {
                    setCartQuantity(1, productUuid ?? "", eventRef, start);
                  }
                  return;
                }

                const newQuantity = clampQuantityToMinAndCap(
                  numericValue,
                  eventRef ? minQty : minOrderQty,
                  maxAvailable
                );
                setCartQuantity(newQuantity, productUuid ?? "", eventRef, start);
              }}
              size={Math.max(quantity.toString().length, 1)}
               style={{
                backgroundColor: "transparent",
                border: "none",
                color: "#333",
              }}
            /> 
            <span>{unitShortname}</span>
          </div>

          {isAutoExtra && (
            <span className="cartQtyStepperSpacer" aria-hidden="true" />
          )}

          {!isAutoExtra && (
            <AddIcon
              style={{
                opacity: qtyControlsDisabled
                  ? 0.35
                  : canIncreaseQuantity
                    ? 1
                    : 0.4,
                cursor:
                  qtyControlsDisabled || !canIncreaseQuantity
                    ? "not-allowed"
                    : "pointer",
              }}
              className={`icon padding-5 fs-14${qtyControlsDisabled ? "" : " clickable"}`}
              aria-disabled={qtyControlsDisabled || !canIncreaseQuantity}
              onClick={() => {
                if (qtyControlsDisabled) return;
                if (!canIncreaseQuantity) {
                  return;
                }

                if (start) {
                  setCartQuantity(
                    quantity + 1,
                    productUuid ?? "",
                    eventRef,
                    start,
                  );
                } else {
                  increaseCartQuantity(
                    productUuid ?? "",
                    eventRef,
                    start,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    minQty > 1 ? minQty : undefined
                  );
                }
              }}
            />
          )}
            </div>

            {!isAutoExtra &&
              start &&
              maxAvailable !== null &&
              quantity > maxAvailable && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "white",
                    fontWeight: "600",
                    backgroundColor: "#AE0000",
                    padding: "3px 15px",
                    borderRadius: "var(--shop-border-radius)",
                  }}
                >
                  Saadaval {maxAvailable}
                </div>
              )}
          </>
        )}
      </div>
      <div className="cartOrderProductRowPrice cartOrderProductRowPrice--regular">
        <span style={{ width: "100%" }}>
          {pf(lineRegularTotal)} &euro;
        </span>
        {!readOnly && hasAdjustmentDisplay && hasAvailabilityInfo && (
          <span
            className="cartOrderProductRowPriceAvailabilitySpacer"
            aria-hidden="true"
          >
            &nbsp;
          </span>
        )}
        {hasAdjustmentDisplay && (
          <span
            className="discount-price-color"
            style={{ width: "100%" }}
          >
            {formatDiscountLineAmount(lineDiscountAmount)} &euro;
          </span>
        )}
      </div>
      {!readOnly && (
        <div
          className="cartOrderProductRowDelete"
          style={{
            visibility: hideDelete ? "hidden" : "visible",
            pointerEvents: hideDelete ? "none" : "auto",
          }}
        >
          <FontAwesomeIcon
            icon={faTrashAlt}
            className="trash-icon"
            onClick={() => removeFromCart(productUuid ?? "", eventRef)}
          />
        </div>
      )}
    </div>
  );
}
